/**
 * 序列化：功能数据 / 设计器数据的分家 —— 纯逻辑。
 *
 * ## 为什么要有这一层
 *
 * 模板最终要交给 C++ 运行端。设计器里有一堆**只有编辑器关心**的东西
 * （节点宽度、位置、框归属、注释正文、折叠状态…），运行端看到它们
 * 只会困惑，或者更糟：某天有人当真了。
 *
 * 所以约定是：**设计器数据全部收进 `design` 子对象，运行端整个丢掉。**
 *
 * ⚠️ 刻意**不用字段白名单**。白名单要每加一个字段就补一条，
 * 漏一条就是「一个显示字段悄悄进了 RenderTree」—— 不报错，也没人会发现。
 * 这里靠的是 `types/node.ts` 里 `FunctionalNode & DesignNode` 的**结构拆分**：
 * 加字段时编译器会逼你归到其中一边。下面的两张键表只是把那个结构
 * 在**运行时**再声明一遍，测试会比对它们和真实模型是否一致。
 */

import type {
  DesignNode,
  FunctionalNode,
  GraphNodeModel,
  NodeCategory,
  SubGraph,
} from '../types/node'
/* 边 id 的命名约定只从这儿来（见 `edgeOps.ts`） */
import { edgeIdOfEndpoints } from './edgeOps'

/** 文档格式版本。改结构时 +1，读的时候好做兼容 */
export const DOCUMENT_VERSION = 1

/** 功能数据的键 —— 运行端**只能**看到这些 */
export const FUNCTIONAL_KEYS = ['id', 'type', 'rows', 'muted'] as const

/** 设计器数据的键 —— 运行端**一个都不该**看到 */
export const DESIGN_KEYS = [
  'title',
  'label',
  'category',
  'render',
  'editorOnly',
  'width',
  'height',
  'note',
  'frameId',
  'hideUnused',
  /* ⚠️ 叠放次序：纯外观（改它不改输出），所以是设计器数据 */
  'z',
] as const

/* ══════════════════════════════════════════════════════════════
   文档结构
   ══════════════════════════════════════════════════════════════ */

/** 位置也是设计器数据（运行端的位置由布局节点决定，跟编辑器坐标无关） */
export interface NodePosition {
  x: number
  y: number
}

/** 设计器数据在文档里的样子 —— 比模型多两项：位置和折叠 */
export interface SerializedDesign extends DesignNode {
  position?: NodePosition
  collapsed?: boolean
}

/**
 * 子图在文档里的样子。
 *
 * 和 `SubGraph` 的区别只有一处：内层节点也做了功能 / 设计拆分了 ——
 * 不然「组里的节点宽度」会顺着 `graph` 漏进 RenderTree。
 * （拆一层不够，得**逐层**拆。）
 */
export interface DocumentSubGraph {
  nodes: DocumentNode[]
  edges: DocumentEdge[]
  /** 内层位置也是设计器数据 */
  positions?: Record<string, NodePosition>
}

export interface DocumentNode extends Omit<FunctionalNode, 'graph'> {
  /**
   * **这个节点来自哪个包**（`NodePackage.id`）。
   *
   * ⚠️ 它是**文档自带的"来源"信息**，不是求值需要的数据：
   * 有了它，一份模板就能自己说清"我依赖哪些包"——
   * 不必先有注册表才能回答（工具侧的完备性检查、跨机器打开别人的模板
   * 都要这个答案）。缺省 = 这个类型不在任何装过的包里
   * （别人手写的文档、或者一个已经卸载的包贡献的类型）。
   *
   * ⚠️ 只记**来源**，不记详情：节点长什么样（`rows`）本来就在文档里，
   * 而"这个包里还有哪些节点"是注册表的事，**不抄进文档** ——
   * 抄了就会漂（升级包之后老文档里那份清单是旧的）。
   */
  pkg?: string
  /**
   * 内部子图。**这是功能数据** —— 里面装的是真正参与求值的节点，
   * 运行端必须拿到它。所以它留在顶层，不在 `design` 里。
   */
  graph?: DocumentSubGraph
  /**
   * 设计器数据。**运行端整个丢掉这个键。**
   *
   * 缺省时也要能读（老文档 / 手写的 IR）。
   */
  design?: SerializedDesign
}

export interface DocumentEdge {
  id: string
  source: string
  sourceHandle: string | null
  target: string
  targetHandle: string | null
  /** 静音**会改变求值结果**，所以是功能数据，在顶层 */
  muted?: boolean
}

export interface GraphDocument {
  version: number
  nodes: DocumentNode[]
  edges: DocumentEdge[]
}

/* ══════════════════════════════════════════════════════════════
   写入
   ══════════════════════════════════════════════════════════════ */

function pickFunctional(model: GraphNodeModel): Omit<FunctionalNode, 'graph'> {
  return {
    id: model.id,
    type: model.type,
    // rows 是端口定义 + 控件默认值，都影响求值
    rows: model.rows,
    ...(model.muted === undefined ? {} : { muted: model.muted }),
  }
}

/**
 * 子图：**逐层**做同样的功能 / 设计拆分。
 *
 * 只拆顶层是不够的 —— 「组里的节点宽度、注释正文、框归属」会顺着
 * `graph` 整个漏进 RenderTree。所以这里递归。
 */
function pickSubGraph(graph: SubGraph, packageOf: PackageLookup): DocumentSubGraph {
  return {
    nodes: graph.nodes.map((model) => {
      const design = pickDesign(model)
      const pkg = packageOf(model.type)
      return {
        ...pickFunctional(model),
        ...(pkg ? { pkg } : {}),
        ...(model.graph ? { graph: pickSubGraph(model.graph, packageOf) } : {}),
        design,
      }
    }),
    edges: graph.edges.map((edge) => ({
      /* ⚠️ id 的命名约定只从 `edgeOps` 来（原来五处各写一遍） */
      id: edgeIdOfEndpoints(edge),
      source: edge.source,
      sourceHandle: edge.sourceHandle ?? null,
      target: edge.target,
      targetHandle: edge.targetHandle ?? null,
      ...(edge.muted === undefined ? {} : { muted: edge.muted }),
    })),
    ...(graph.positions ? { positions: graph.positions } : {}),
  }
}

function pickDesign(model: GraphNodeModel): SerializedDesign {
  const design: Record<string, unknown> = {}
  for (const key of DESIGN_KEYS) {
    const value = (model as unknown as Record<string, unknown>)[key]
    if (value !== undefined) design[key] = value
  }
  return design as unknown as SerializedDesign
}

/** 图引擎节点（应用层的形状）→ 文档节点 */
export interface SourceNode {
  id: string
  position?: NodePosition
  data?: { collapsed?: boolean; model?: GraphNodeModel }
}

/**
 * 「这个节点类型来自哪个包」——**由宿主注入**。
 *
 * ⚠️ 为什么不在这里 import 装配点：`graph/` 不许依赖 `playground/`
 * （`verify:layers` 守着的方向）。而且"有哪些包"是宿主才知道的事 ——
 * 这一层只负责把答案写进文档。
 */
export type PackageLookup = (type: string) => string | undefined

export function toDocument(
  nodes: readonly SourceNode[],
  edges: readonly (DocumentEdge & { [key: string]: unknown })[],
  /** 不给就不写 `pkg`（测试、手写文档的场景） */
  options: { packageOf?: PackageLookup } = {},
): GraphDocument {
  const packageOf: PackageLookup = options.packageOf ?? (() => undefined)
  const documentNodes: DocumentNode[] = []

  for (const node of nodes) {
    const model = node.data?.model
    if (!model) continue

    const design = pickDesign(model)
    if (node.position) design.position = { x: node.position.x, y: node.position.y }
    if (node.data?.collapsed !== undefined) design.collapsed = node.data.collapsed

    const pkg = packageOf(model.type)
    documentNodes.push({
      ...pickFunctional(model),
      ...(pkg ? { pkg } : {}),
      ...(model.graph ? { graph: pickSubGraph(model.graph, packageOf) } : {}),
      design,
    })
  }

  return {
    version: DOCUMENT_VERSION,
    nodes: documentNodes,
    edges: edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      sourceHandle: edge.sourceHandle ?? null,
      target: edge.target,
      targetHandle: edge.targetHandle ?? null,
      ...(edge.muted === undefined ? {} : { muted: edge.muted }),
    })),
  }
}

/**
 * **这份文档依赖哪些包** —— 从每个节点记的 `pkg` 推出来（去重、排序）。
 *
 * ⚠️ **推导，不另存一份**：文档里只有逐节点的 `pkg`。
 * 再存一个文档级的清单就是同一个事实写两遍，删掉一个节点就可能漏改
 * （资产那轮已经吃过一次亏：接口存两份必然漂）。
 *
 * ⚠️ 它**包含子图里的节点**（组/循环内部的节点同样需要它们的包），
 * 而且是给**工具侧完备性检查**用的：缺任何一个包就该拒绝执行，
 * 而不是"跳过算不出来的节点继续打"（见 `docs/node-packages.md` §6.6）。
 */
export function documentPackages(document: GraphDocument): string[] {
  const found = new Set<string>()
  const walk = (nodes: readonly DocumentNode[]): void => {
    for (const node of nodes) {
      if (node.pkg) found.add(node.pkg)
      if (node.graph) walk(node.graph.nodes)
    }
  }
  walk(document.nodes)
  return [...found].sort()
}

/* ══════════════════════════════════════════════════════════════
   RenderTree
   ══════════════════════════════════════════════════════════════ */

/**
 * IR 里的子图。
 *
 * ⚠️ 它和内存里的 `SubGraph` **不是一个东西**：`SubGraph.nodes` 是完整的
 * `GraphNodeModel`（设计数据内联着），而 IR 里每一层都已经被剥过了。
 * 硬把两者塞进同一个类型，等于放弃了「IR 里不可能有设计数据」这个保证。
 */
export interface IrSubGraph {
  nodes: IrNode[]
  edges: DocumentEdge[]
}

export interface IrNode extends Omit<FunctionalNode, 'graph'> {
  graph?: IrSubGraph
}

export interface RenderTree {
  version: number
  nodes: IrNode[]
  edges: DocumentEdge[]
}

/**
 * 交给运行端的 IR：**只有功能数据**。
 *
 * 三件事：
 *   1. 丢掉 `design` 整个键（机械地，不靠逐字段判断）
 *   2. **丢掉编辑器专用节点**（框 / 注释）—— 它们不产出任何东西。
 *      连带的边也一起丢掉，否则 IR 里会有指向不存在节点的边。
 *   3. **子图逐层做同样的事** —— 组里的框、注释、宽度同样不该进 IR。
 */
export function toRenderTree(
  document: GraphDocument,
  isEditorOnly: (node: DocumentNode) => boolean,
): RenderTree {
  function prune(
    nodes: readonly DocumentNode[],
    edges: readonly DocumentEdge[],
  ): { nodes: IrNode[]; edges: DocumentEdge[] } {
    const kept = nodes.filter((node) => !isEditorOnly(node))
    const keptIds = new Set(kept.map((node) => node.id))

    return {
      // 逐字段重建，而不是删 key —— 这样**新增字段默认不会泄漏**：
      // 忘了处理新字段的后果是「IR 里少了它」，而不是「设计数据漏出去了」。
      nodes: kept.map((node) => ({
        id: node.id,
        type: node.type,
        rows: node.rows,
        ...(node.muted === undefined ? {} : { muted: node.muted }),
        ...(node.graph ? { graph: prune(node.graph.nodes, node.graph.edges) } : {}),
      })),
      edges: edges.filter(
        (edge) => keptIds.has(edge.source) && keptIds.has(edge.target),
      ),
    }
  }

  const pruned = prune(document.nodes, document.edges)
  return { version: document.version, nodes: pruned.nodes, edges: pruned.edges }
}

/* ══════════════════════════════════════════════════════════════
   读回
   ══════════════════════════════════════════════════════════════ */

/** 文档节点 → 图引擎节点（位置从 design 里取回来） */
export function fromDocument(
  document: GraphDocument,
): { nodes: SourceNode[]; edges: DocumentEdge[] } {
  const nodes: SourceNode[] = document.nodes.map((node) => {
    const { design, ...functional } = node
    const model = { ...functional, ...(design ?? {}) } as GraphNodeModel
    return {
      id: node.id,
      position: design?.position ?? { x: 0, y: 0 },
      data: { collapsed: design?.collapsed ?? false, model },
    }
  })
  return { nodes, edges: document.edges }
}

/**
 * 模型上出现了一个**没被分类**的字段。
 *
 * 测试里用它当断言：加字段忘了归类时，这里会报出来。
 * （类型层面的 `FunctionalNode & DesignNode` 已经逼着分类了，
 *   这个函数防的是「两张键表和接口走偏」。）
 */
export function unclassifiedKeys(model: Record<string, unknown>): string[] {
  const known = new Set<string>([...FUNCTIONAL_KEYS, ...DESIGN_KEYS])
  return Object.keys(model).filter((key) => !known.has(key))
}

/**
 * 给测试用：造一个把两个接口的字段都填满的模型。
 *
 * ⚠️ 类型标识和分类都用**中性的假值**，不用真实节点。
 * 原来这里写的是 `'component.text'` / `'component'` —— 那是**领域包的
 * 节点标识出现在内核里**：内核（序列化）不该知道任何一个真实的包，
 * 否则「可选加载」一上来就漏了（关掉那个包，这个内核文件就没意义了）。
 * 它只是往返测试的夹具，随便什么标识都能测。
 */
export function sampleModel(): GraphNodeModel {
  return {
    // 功能
    id: 'n1',
    type: 'test.node',
    rows: [],
    muted: true,
    // 设计
    title: '测试节点',
    label: '自定义',
    category: 'utility' as NodeCategory,
    render: 'default',
    editorOnly: false,
    width: 200,
    height: 100,
    note: '注释',
    frameId: 'F1',
    hideUnused: true,
  }
}
